import { isAxiosNotFoundError } from "@/api";
import {
    addSkillFile,
    checkSkillName,
    deleteSkillFile,
    getSkill,
    publishSkill,
    snapshotSkill,
    updateSkill,
    updateSkillFile,
    type ISkill,
    type ISkillVersion,
} from "@/api/skills";
import { isAxiosError } from "axios";
import { useCallback } from "react";

import { type SkillEditorReferences } from "../skill-editor-references";
import {
    applySkillEditorOperationReceipt,
    createSkillEditorRetryBlockedRecovery,
} from "../skill-editor-save-conflicts";
import {
    markSkillEditorVisibleFilesSeeded,
    preflightSkillEditorSave,
} from "../skill-editor-save-reconciliation";
import {
    captureSkillEditorPublishAttempt,
    classifySkillEditorPublishFailure,
    evaluateSkillEditorPublishFailure,
} from "../skill-editor-publication";
import {
    matchesSkillEditorNameCheck,
    planSkillEditorSave,
    SkillEditorSaveValidationError,
    type SkillEditorOperationReceipt,
    type SkillEditorSaveConflict,
    type SkillEditorSaveIntent,
    type SkillEditorSaveOperation,
    type SkillEditorSavePlan,
    type SkillEditorSaveRecovery,
    type SkillEditorSaveStatus,
} from "../skill-editor-save";
import {
    clearSkillEditorReviewRequired,
    resetSkillEditorState,
    type SkillEditorState,
} from "../skill-editor-state";

export interface SkillEditorSaveResult {
    plan: SkillEditorSavePlan;
    snapshot: ISkillVersion | null;
    publishedSkill: ISkill | null;
    noChanges: boolean;
}

export class SkillEditorSaveRecoveryRequiredError extends Error {
    readonly recovery: SkillEditorSaveRecovery;

    constructor(recovery: SkillEditorSaveRecovery) {
        super("Resolve the previous save before trying again");
        this.name = "SkillEditorSaveRecoveryRequiredError";
        this.recovery = recovery;
    }
}

export interface SkillEditorSaveConflictView extends SkillEditorSaveConflict {
    readonly intent: SkillEditorSaveIntent;
    readonly supersededAcknowledgment: boolean;
}

export class SkillEditorSaveConflictError extends Error {
    readonly conflict: SkillEditorSaveConflictView;

    constructor(conflict: SkillEditorSaveConflictView) {
        super("Resolve conflicting changes before saving");
        this.name = "SkillEditorSaveConflictError";
        this.conflict = conflict;
    }
}

export interface OwnedSkillEditorSaveConflict {
    readonly skillId: number;
    readonly routeEpoch: number;
    readonly intent: SkillEditorSaveIntent;
    readonly conflict: SkillEditorSaveConflictView;
}

export interface SkillEditorSaveFailureContext {
    readonly skillId: number;
    readonly routeEpoch: number;
    readonly intent: SkillEditorSaveIntent;
    readonly error: unknown;
    readonly completedOperations: readonly SkillEditorOperationReceipt[];
    readonly failedOperation: SkillEditorSaveOperation | null;
}

export interface OwnedSkillEditorSaveRecovery extends SkillEditorSaveFailureContext {
    readonly recovery: SkillEditorSaveRecovery;
}

interface CurrentRef<Value> {
    current: Value;
}

type StateTransition = (state: SkillEditorState) => SkillEditorState;

interface UseSkillEditorSaveExecutionOptions {
    readonly stateRef: CurrentRef<SkillEditorState>;
    readonly routeEpochRef: CurrentRef<number>;
    readonly pendingSourceRef: CurrentRef<ISkill | null>;
    readonly pendingSnapshotSkillIdsRef: CurrentRef<ReadonlySet<number>>;
    readonly saveRecoveryRef: CurrentRef<OwnedSkillEditorSaveRecovery | null>;
    readonly acknowledgedConflictRef: CurrentRef<{
        skillId: number;
        routeEpoch: number;
        token: string;
    } | null>;
    readonly referencesForState: (
        state: SkillEditorState
    ) => SkillEditorReferences;
    readonly transition: (apply: StateTransition) => void;
    readonly cacheAuthoritativeSkill: (
        source: ISkill,
        routeEpoch: number
    ) => void;
    readonly storeSaveConflict: (
        conflict: OwnedSkillEditorSaveConflict | null
    ) => void;
    readonly storeSaveRecovery: (
        recovery: OwnedSkillEditorSaveRecovery | null
    ) => void;
    readonly storeSaveStatus: (
        status: SkillEditorSaveStatus,
        skillId: number,
        routeEpoch: number
    ) => void;
    readonly setSnapshotPending: (skillId: number, pending: boolean) => void;
    readonly cancelDetailsQuery: (skillId: number) => Promise<void>;
    readonly invalidateAfterMutation: (
        skillId: number,
        scopeChanged?: boolean
    ) => Promise<void>;
    readonly reconcileFailedSave: (
        authoritative: ISkill,
        context: SkillEditorSaveFailureContext
    ) => SkillEditorSaveRecovery | null;
    readonly preserveSkillEditorClientKeys: (
        previous: SkillEditorState,
        replacement: SkillEditorState
    ) => SkillEditorState;
    readonly preserveIdlessSkillEditorFiles: (
        previous: SkillEditorState,
        replacement: SkillEditorState
    ) => SkillEditorState;
}

export const useSkillEditorSaveExecution = ({
    stateRef,
    routeEpochRef,
    pendingSourceRef,
    pendingSnapshotSkillIdsRef,
    saveRecoveryRef,
    acknowledgedConflictRef,
    referencesForState,
    transition,
    cacheAuthoritativeSkill,
    storeSaveConflict,
    storeSaveRecovery,
    storeSaveStatus,
    setSnapshotPending,
    cancelDetailsQuery,
    invalidateAfterMutation,
    reconcileFailedSave,
    preserveSkillEditorClientKeys,
    preserveIdlessSkillEditorFiles,
}: UseSkillEditorSaveExecutionOptions) => {
    const executeSave = useCallback(
        async (
            intent: SkillEditorSaveIntent
        ): Promise<SkillEditorSaveResult> => {
            const current = stateRef.current;
            const routeEpoch = routeEpochRef.current;
            const acknowledgedConflict = acknowledgedConflictRef.current;
            acknowledgedConflictRef.current = null;
            const ownsRoute = () =>
                stateRef.current.skillId === current.skillId &&
                routeEpochRef.current === routeEpoch;
            const getAuthoritativeSkill = async (skillId: number) => {
                await cancelDetailsQuery(skillId);
                try {
                    return await getSkill(skillId);
                } finally {
                    await cancelDetailsQuery(skillId);
                }
            };
            const blockedRecovery = saveRecoveryRef.current;
            if (
                blockedRecovery?.skillId === current.skillId &&
                blockedRecovery.routeEpoch === routeEpoch
            ) {
                throw new SkillEditorSaveRecoveryRequiredError(
                    blockedRecovery.recovery
                );
            }
            storeSaveStatus({ kind: "idle" }, current.skillId, routeEpoch);
            const initialPlan = planSkillEditorSave(
                current,
                intent,
                referencesForState(current)
            );
            const hasPendingSnapshot = pendingSnapshotSkillIdsRef.current.has(
                current.skillId
            );
            if (initialPlan.operations.length === 0 && !hasPendingSnapshot) {
                transition(clearSkillEditorReviewRequired);
                storeSaveStatus({ kind: "saved" }, current.skillId, routeEpoch);
                return {
                    plan: initialPlan,
                    snapshot: null,
                    publishedSkill: null,
                    noChanges: true,
                };
            }

            const authoritative = await getAuthoritativeSkill(current.skillId);
            const liveState =
                stateRef.current.skillId === current.skillId &&
                routeEpochRef.current === routeEpoch
                    ? stateRef.current
                    : current;
            const acknowledgedRemoteToken =
                acknowledgedConflict?.skillId === current.skillId &&
                acknowledgedConflict.routeEpoch === routeEpoch
                    ? acknowledgedConflict.token
                    : null;
            const preflight = preflightSkillEditorSave(
                liveState,
                authoritative,
                intent,
                acknowledgedRemoteToken
            );
            if (preflight.kind === "conflict") {
                const conflict: SkillEditorSaveConflictView = {
                    ...preflight.conflict,
                    intent,
                    supersededAcknowledgment:
                        acknowledgedRemoteToken !== null &&
                        acknowledgedRemoteToken !==
                            preflight.conflict.fence.token,
                };
                const ownedConflict = {
                    skillId: current.skillId,
                    routeEpoch,
                    intent,
                    conflict,
                };
                if (ownsRoute()) storeSaveConflict(ownedConflict);
                throw new SkillEditorSaveConflictError(conflict);
            }
            if (preflight.kind === "invalid") {
                if (ownsRoute()) {
                    storeSaveConflict(null);
                    cacheAuthoritativeSkill(authoritative, routeEpoch);
                    transition(() =>
                        markSkillEditorVisibleFilesSeeded(
                            preflight.state,
                            authoritative
                        )
                    );
                }
                throw new SkillEditorSaveValidationError(preflight.validation);
            }
            if (ownsRoute()) {
                storeSaveConflict(null);
                cacheAuthoritativeSkill(authoritative, routeEpoch);
                transition(() =>
                    markSkillEditorVisibleFilesSeeded(
                        preflight.state,
                        authoritative
                    )
                );
            }
            const planned = preflight.plan;
            const plan: SkillEditorSavePlan =
                hasPendingSnapshot &&
                !planned.operations.some(
                    (operation) => operation.kind === "snapshot"
                )
                    ? {
                          ...planned,
                          hasVersionedChanges: true,
                          operations: [
                              { kind: "snapshot", skillId: current.skillId },
                              ...planned.operations,
                          ],
                      }
                    : planned;
            if (plan.operations.length === 0) {
                if (ownsRoute()) {
                    transition(clearSkillEditorReviewRequired);
                    storeSaveStatus(
                        { kind: "saved" },
                        current.skillId,
                        routeEpoch
                    );
                }
                return {
                    plan,
                    snapshot: null,
                    publishedSkill: null,
                    noChanges: true,
                };
            }
            const publishAttempt = captureSkillEditorPublishAttempt(plan);
            const scopeChanged = plan.diff.skillFields.some(
                (field) =>
                    field === "scopeKind" ||
                    field === "organizationId" ||
                    field === "organizationGroupId"
            );

            const nameCheckMatches = () =>
                routeEpochRef.current === routeEpoch &&
                matchesSkillEditorNameCheck(stateRef.current, plan.nameCheck);
            let nameAvailability: Awaited<ReturnType<typeof checkSkillName>>;
            try {
                nameAvailability = await checkSkillName(
                    plan.nameCheck.name,
                    plan.nameCheck.excludeId
                );
            } catch (error) {
                if (!nameCheckMatches()) {
                    throw new Error(
                        "The skill name changed while availability was checked. Try saving again."
                    );
                }
                throw error;
            }
            if (!nameCheckMatches()) {
                throw new Error(
                    "The skill name changed while availability was checked. Try saving again."
                );
            }
            if (!nameAvailability.available) {
                throw new Error("A skill with this name already exists");
            }

            pendingSourceRef.current = null;
            const savedSkillId = plan.operations[0].skillId;
            let snapshot: ISkillVersion | null = null;
            let publishedSkill: ISkill | null = null;
            const completedOperations: SkillEditorOperationReceipt[] = [];
            const versionedSkillUpdate = plan.diff.skillFields.some((field) =>
                ["name", "description", "content"].includes(field)
            );
            const createsSnapshotDebt = (operation: SkillEditorSaveOperation) =>
                operation.kind === "delete-file" ||
                operation.kind === "update-file" ||
                operation.kind === "add-file" ||
                (operation.kind === "update-skill" && versionedSkillUpdate);
            const recordReceipt = (receipt: SkillEditorOperationReceipt) => {
                completedOperations.push(receipt);
                if (
                    createsSnapshotDebt(receipt.operation) &&
                    !(
                        receipt.kind === "delete-file" &&
                        receipt.outcome === "already-missing"
                    )
                ) {
                    setSnapshotPending(receipt.operation.skillId, true);
                }
                transition((current) =>
                    current.skillId === savedSkillId &&
                    routeEpochRef.current === routeEpoch
                        ? applySkillEditorOperationReceipt(current, receipt)
                        : current
                );
            };
            let failedOperation: SkillEditorSaveOperation | null = null;
            try {
                for (const operation of plan.operations) {
                    failedOperation = operation;
                    switch (operation.kind) {
                        case "update-skill": {
                            const savedSkill = await updateSkill(
                                operation.skillId,
                                operation.payload
                            );
                            recordReceipt({
                                kind: "update-skill",
                                operation,
                                skill: savedSkill,
                            });
                            break;
                        }
                        case "delete-file": {
                            let outcome: "deleted" | "already-missing" =
                                "deleted";
                            try {
                                await deleteSkillFile(
                                    operation.skillId,
                                    operation.fileId
                                );
                            } catch (error) {
                                if (!isAxiosNotFoundError(error)) throw error;
                                outcome = "already-missing";
                            }
                            recordReceipt({
                                kind: "delete-file",
                                operation,
                                outcome,
                            });
                            break;
                        }
                        case "update-file": {
                            const savedFile = await updateSkillFile(
                                operation.skillId,
                                operation.fileId,
                                operation.payload
                            );
                            recordReceipt({
                                kind: "update-file",
                                operation,
                                file: savedFile,
                            });
                            break;
                        }
                        case "add-file": {
                            const savedFile = await addSkillFile(
                                operation.skillId,
                                operation.payload
                            );
                            recordReceipt({
                                kind: "add-file",
                                operation,
                                file: savedFile,
                            });
                            break;
                        }
                        case "snapshot":
                            try {
                                snapshot = await snapshotSkill(
                                    operation.skillId
                                );
                                setSnapshotPending(operation.skillId, false);
                            } catch (error) {
                                setSnapshotPending(operation.skillId, true);
                                throw error;
                            }
                            recordReceipt({
                                kind: "snapshot",
                                operation,
                            });
                            break;
                        case "publish": {
                            const savedSkill = await publishSkill(
                                operation.skillId
                            );
                            publishedSkill = savedSkill;
                            recordReceipt({
                                kind: "publish",
                                operation,
                                skill: savedSkill,
                            });
                            break;
                        }
                    }
                    failedOperation = null;
                }
            } catch (error) {
                const responseStatus = isAxiosError(error)
                    ? error.response?.status
                    : undefined;
                const definitelyRejected =
                    classifySkillEditorPublishFailure(responseStatus) ===
                    "rejected";
                if (
                    failedOperation &&
                    createsSnapshotDebt(failedOperation) &&
                    !definitelyRejected
                ) {
                    setSnapshotPending(failedOperation.skillId, true);
                }
                if (
                    stateRef.current.skillId === savedSkillId &&
                    routeEpochRef.current === routeEpoch
                ) {
                    pendingSourceRef.current = null;
                }
                const context: SkillEditorSaveFailureContext = {
                    skillId: savedSkillId,
                    routeEpoch,
                    intent,
                    error,
                    completedOperations: [...completedOperations],
                    failedOperation,
                };
                if (failedOperation?.kind === "publish" && publishAttempt) {
                    let authoritative: ISkill | null = null;
                    try {
                        await invalidateAfterMutation(
                            savedSkillId,
                            scopeChanged
                        );
                        authoritative =
                            await getAuthoritativeSkill(savedSkillId);
                    } catch {
                        authoritative = null;
                    }
                    const outcome = evaluateSkillEditorPublishFailure({
                        state: ownsRoute() ? stateRef.current : current,
                        attempt: publishAttempt,
                        completedOperations,
                        error,
                        responseStatus,
                        authoritative,
                    });
                    if (
                        stateRef.current.skillId === savedSkillId &&
                        routeEpochRef.current === routeEpoch
                    ) {
                        if (authoritative) {
                            cacheAuthoritativeSkill(authoritative, routeEpoch);
                        }
                        transition(() => outcome.state);
                        storeSaveConflict(null);
                        storeSaveRecovery(
                            outcome.kind === "status-unknown" ||
                                outcome.kind ===
                                    "draft-saved-publish-rejected" ||
                                outcome.kind === "unpublished-elsewhere"
                                ? {
                                      ...context,
                                      recovery: outcome.recovery,
                                  }
                                : null
                        );
                        if (outcome.kind === "applied") {
                            storeSaveStatus(
                                { kind: "saved" },
                                savedSkillId,
                                routeEpoch
                            );
                        }
                    }
                    if (outcome.kind === "applied") {
                        publishedSkill = authoritative;
                        return {
                            plan,
                            snapshot,
                            publishedSkill,
                            noChanges: false,
                        };
                    }
                    throw error;
                }
                let recovery: SkillEditorSaveRecovery | null = null;
                try {
                    await invalidateAfterMutation(savedSkillId, scopeChanged);
                    const authoritative =
                        await getAuthoritativeSkill(savedSkillId);
                    recovery = reconcileFailedSave(authoritative, context);
                } catch (reconcileError) {
                    recovery = createSkillEditorRetryBlockedRecovery(
                        completedOperations,
                        reconcileError
                    );
                }
                if (
                    stateRef.current.skillId === savedSkillId &&
                    routeEpochRef.current === routeEpoch
                ) {
                    storeSaveConflict(null);
                    storeSaveRecovery(
                        recovery
                            ? {
                                  ...context,
                                  recovery,
                              }
                            : null
                    );
                }
                throw error;
            }

            transition((current) =>
                current.skillId === savedSkillId &&
                routeEpochRef.current === routeEpoch
                    ? clearSkillEditorReviewRequired(current)
                    : current
            );
            if (ownsRoute()) {
                storeSaveRecovery(null);
                storeSaveStatus({ kind: "saved" }, savedSkillId, routeEpoch);
            }
            if (publishedSkill) {
                cacheAuthoritativeSkill(publishedSkill, routeEpoch);
            }

            let latestSkill: ISkill | null = null;
            try {
                latestSkill = await getAuthoritativeSkill(savedSkillId);
            } catch (error) {
                if (
                    stateRef.current.skillId === savedSkillId &&
                    routeEpochRef.current === routeEpoch
                ) {
                    pendingSourceRef.current = null;
                    storeSaveStatus(
                        { kind: "saved-with-refresh-warning", error },
                        savedSkillId,
                        routeEpoch
                    );
                }
            }
            if (latestSkill) {
                cacheAuthoritativeSkill(latestSkill, routeEpoch);
                transition((current) => {
                    if (
                        current.skillId !== latestSkill.id ||
                        routeEpochRef.current !== routeEpoch
                    ) {
                        return current;
                    }
                    if (current.editGeneration !== plan.capturedGeneration) {
                        pendingSourceRef.current = latestSkill;
                        return markSkillEditorVisibleFilesSeeded(
                            current,
                            latestSkill
                        );
                    }
                    return preserveIdlessSkillEditorFiles(
                        current,
                        preserveSkillEditorClientKeys(
                            current,
                            resetSkillEditorState(current, latestSkill, {
                                reviewRequired: false,
                            })
                        )
                    );
                });
            }
            await invalidateAfterMutation(savedSkillId, scopeChanged);
            return { plan, snapshot, publishedSkill, noChanges: false };
        },
        [
            acknowledgedConflictRef,
            cacheAuthoritativeSkill,
            cancelDetailsQuery,
            invalidateAfterMutation,
            pendingSnapshotSkillIdsRef,
            pendingSourceRef,
            preserveIdlessSkillEditorFiles,
            preserveSkillEditorClientKeys,
            reconcileFailedSave,
            referencesForState,
            routeEpochRef,
            saveRecoveryRef,
            setSnapshotPending,
            stateRef,
            storeSaveConflict,
            storeSaveRecovery,
            storeSaveStatus,
            transition,
        ]
    );

    return executeSave;
};
